"use client";

import { ReactNode } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { DISCORD_INVITE_URL, KRAKEN_TRADE_HISTORY_PERMISSION_LABEL } from "@cede/utils";
import { InformationModal } from "./InformationModal";
import { Button } from "@/components/ui/button";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useCreateAccountStore } from "@/store/accountStore";
import { FLOW_TYPES, STEPS, useConnectStore } from "@/store/connectStore";
import { useKrakenReconnectRequiredStore } from "@/store/useKrakenReconnectRequiredStore";

const Bold = ({ children }: { children: ReactNode }) => {
  return <span className="font-semibold text-cede-text-primary">{children}</span>;
};

export const KrakenReconnectRequiredModal = () => {
  const { open, setOpen } = useKrakenReconnectRequiredStore();
  const { setOpenConnectModal, setExchangeIds, setFlowType, setActiveStep } = useConnectStore();
  const { updateFormData } = useCreateAccountStore();
  const oAuthSupportMap = useOAuthSupportMap();

  const handleReconnect = () => {
    setExchangeIds([SUPPORTED_EXCHANGES.KRAKEN]);
    setFlowType(FLOW_TYPES.RELINK);
    setActiveStep(oAuthSupportMap[SUPPORTED_EXCHANGES.KRAKEN] ? STEPS.LinkAccount : STEPS.ApiKeys);
    setOpenConnectModal(true);
    updateFormData({ selectedCex: SUPPORTED_EXCHANGES.KRAKEN });
    setOpen(false);
  };

  return (
    <InformationModal open={open} onClose={() => setOpen(false)}>
      <div className="space-y-6 text-base text-cede-text-secondary">
        <div className="space-y-3 leading-relaxed">
          <p>
            <Bold>Your Kraken account was disconnected.</Bold> Cede Hub now needs to read your Kraken trade history, and
            your API key doesn&apos;t allow it.
          </p>
          <p>
            Reconnect Kraken with a new API key that has the <Bold>{KRAKEN_TRADE_HISTORY_PERMISSION_LABEL}</Bold>{" "}
            permission enabled.
          </p>
          <p className="text-sm text-cede-text-tertiary">
            Note: Your trading history and rewards on Cede Hub are preserved.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 pt-2">
          <Button
            variant="primary"
            size="large"
            className="h-12 w-full rounded-2xl text-base font-semibold sm:w-[65%]"
            onClick={handleReconnect}
          >
            Reconnect Kraken
          </Button>
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center text-sm font-medium text-cede-primary underline"
          >
            Need help? Contact our support team
          </a>
        </div>
      </div>
    </InformationModal>
  );
};
